import { Button, Drawer, Spin, Tabs, Typography, message, Input } from 'antd'; 
import React, { useCallback, useEffect, useState, useMemo } from 'react'; 
import { getApplicantByEmailApiRequest } from '../../../api/applicant';
import EnrollmentInfo from './EnrollmentInfo';
import PersonalInfo from './PersonalInfo';
import StatusInfo from './StatusInfo';

import RequirementsSection from './RequirementsSection';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Drawer component for reviewing applicant details
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the drawer is visible
 * @param {Function} props.onClose - Function to call when drawer is closed
 * @param {Object} props.applicant - Initial applicant data (likely containing at least email and _id)
 * @param {Function} props.onApprove - Function to call when applicant is approved (if applicable here)
 * @param {Function} props.onReject - Function to call when applicant requirements are rejected
 */
const ApplicantReviewDrawer = ({ // Renamed for clarity, was ApplicantReviewModal
  visible,
  onClose,
  applicant, // This 'applicant' prop is the brief data passed when opening the drawer
  // onApprove = () => {}, // Assuming approval logic might be handled elsewhere or not in this specific view
  onReject = () => {},
}) => {
  // Component state
  const [loading, setLoading] = useState(false);
  const [applicantDetails, setApplicantDetails] = useState(null); // This will hold the full fetched details
  const [approvalLoading, setApprovalLoading] = useState(false); // For reject action
  const [activeTab, setActiveTab] = useState('1');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // Fetch full applicant details function using the email from the initial 'applicant' prop
  const fetchApplicantDetails = useCallback(async (email) => {
    if (!email) {
      setApplicantDetails(null); // Clear details if no email
      return;
    }

    setLoading(true);
    try {
      const response = await getApplicantByEmailApiRequest(email);

      if (response.success && response.data) {
        setApplicantDetails(response.data);
      } else {
        message.error(response.message || 'Failed to fetch applicant details');
        setApplicantDetails(null); // Clear details on error
        // Do not auto-close the drawer on fetch error, let user see the message
      }
    } catch (error) {
      console.error('Error fetching applicant details:', error);
      message.error('An error occurred while fetching applicant details.');
      setApplicantDetails(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch detailed applicant information when drawer becomes visible and applicant prop is available
  useEffect(() => {
    if (visible && applicant?.email) {
      fetchApplicantDetails(applicant.email);
    } else if (!visible) {
      // Reset state when drawer closes
      setApplicantDetails(null); // Clear full details
      setActiveTab('1');
      setRejectReason('');
      setShowRejectInput(false);
      setLoading(false); // Ensure loading is reset
      setApprovalLoading(false); // Ensure approval loading is reset
    }
  }, [visible, applicant, fetchApplicantDetails]);


  /**
   * Handle drawer close
   */
  const handleClose = () => {
    // Parent component's onClose will hide the drawer.
    // Internal state reset is now handled in the useEffect based on `visible`.
    onClose();
  };

  /**
   * Handle rejection of requirements
   */
  const handleRejectRequirements = async () => {
    if (!rejectReason || !rejectReason.trim()) {
      message.error('Please provide a reason for rejection');
      return;
    }

    setApprovalLoading(true);
    try {
      // Use applicantDetails._id if available (from full fetch), fallback to applicant._id
      const idToReject = applicantDetails?._id || applicant?._id;
      if (!idToReject) {
        message.error('Applicant ID not found for rejection.');
        setApprovalLoading(false);
        return;
      }
      await onReject(idToReject, rejectReason);
      message.success('Applicant requirements have been marked for rejection review.'); // Or similar success message
      handleClose(); // Close drawer on successful rejection
    } catch (error) {
      console.error('Error rejecting requirements:', error);
      message.error('Failed to reject requirements: ' + (error.message || 'Unknown error'));
    } finally {
      setApprovalLoading(false);
    }
  };

  // Define tab items for the Tabs API
  const tabItems = useMemo(
    () => [
      {
        key: '1',
        label: 'Enrollment Info',
        children: applicantDetails ? <EnrollmentInfo data={applicantDetails} /> : <Spin />,
      },
      {
        key: '2',
        label: 'Applicant Info',
        children: applicantDetails ? <PersonalInfo data={applicantDetails} /> : <Spin />,
      },
       {
        key: '3',
        label: 'Requirements',
        children: applicantDetails ? (
          <RequirementsSection
            admissionRequirements={applicantDetails.admissionRequirements}
            enrollmentRequirements={applicantDetails.enrollmentRequirements || []}
            voucherRequirements={applicantDetails.voucherRequirements || []}
            applicantId={applicantDetails._id}
            enrollmentRequirementsStatus={applicantDetails.enrollmentRequirementsStatus || "Incomplete"}
            admissionRequirementsStatus={applicantDetails.admissionRequirementsStatus || "Incomplete"}
            voucherApplicationStatus={applicantDetails.voucherApplicationStatus || "Incomplete"}
          />
        ) : <Spin />,
      },
      {
        key: '4',
        label: 'Status Summary', // Renamed for clarity
        children: applicantDetails ? <StatusInfo data={applicantDetails} /> : <Spin />,
      },
    ],
    [applicantDetails] // Depends only on the fully fetched applicantDetails
  );

  // Drawer footer buttons
  const renderFooter = () => {
    // If showRejectInput is true, show Cancel and Confirm Rejection
    if (showRejectInput) {
      return (
        <div style={{ textAlign: 'right' }}>
          <Button style={{ marginRight: 8 }} onClick={() => setShowRejectInput(false)}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            loading={approvalLoading}
            onClick={handleRejectRequirements}
          >
            Confirm Rejection
          </Button>
        </div>
      );
    }

    // Default footer: Close button and potentially a "Reject Requirements" button
    // This example assumes "Reject Requirements" button is always available if details are loaded
    // You might want to conditionally show it based on statuses in applicantDetails
    return (
      <div style={{ textAlign: 'right' }}>
        <Button style={{ marginRight: 8 }} onClick={handleClose}>
          Close
        </Button>
        {applicantDetails && ( // Only show if details are loaded
          <Button
            type="dashed"
            danger
            onClick={() => setShowRejectInput(true)}
            // Disable if already rejected or in a state where rejection is not possible
            // disabled={applicantDetails?.someRejectionStatus === 'Rejected'}
          >
            Reject Requirements
          </Button>
        )}
      </div>
    );
  };
  
  const drawerTitle = (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Title level={4} style={{ margin: 0 }}>
        Applicant Details Review
      </Title>
      {applicant && ( // Show basic info from initial prop while full details load
         <Text type="secondary">
            {`${applicant.firstName || ''} ${applicant.lastName || ''} (${applicant.email || 'N/A'})`}
        </Text>
      )}
      {applicantDetails && applicant?.email !== applicantDetails?.email && ( // If full details have different info (unlikely but good check)
         <Text type="secondary" style={{fontSize: '0.9em', color: 'orange'}}>
            Displaying full details for: {`${applicantDetails.firstName} ${applicantDetails.lastName} (${applicantDetails.email})`}
        </Text>
      )}
    </div>
  );


  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      onClose={handleClose}
      open={visible}
      width={720} // Adjust width as needed, e.g., '50%', 720, 900
      destroyOnClose // Important to reset state of children components if not unmounted
      footer={renderFooter()} // Use the dynamic footer
      styles={{ // antd v5 Drawer styles prop
        body: {
          padding: '20px', // Adjust padding if needed
          height: 'calc(100vh - 128px)', // Adjust based on header and footer height (approx 64px each)
          overflowY: 'auto',
        },
      }}
    >
      <Spin spinning={loading && !applicantDetails} tip="Loading applicant details...">
        {/* If loading and no details yet, Spin covers all. If loading but old details exist, show old and update. */}
        {/* If not loading, show content or 'No details' message. */}
        {!loading && !applicantDetails && applicant?.email && (
           <Text type="secondary">Could not load details for {applicant.email}.</Text>
        )}
        {!applicant?.email && !applicantDetails && (
            <Text type="secondary">No applicant selected or email missing.</Text>
        )}
        {(applicantDetails || (loading && applicantDetails)) && ( // Show tabs if details exist, even if refreshing
          showRejectInput ? (
            <div style={{ marginTop: '20px' }}> {/* Adjusted margin */}
              <Title level={5}>Provide Reason for Rejection</Title>
              <TextArea
                rows={4}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please explain why the requirements are being rejected..."
              />
            </div>
          ) : (
            <Tabs
              defaultActiveKey="1"
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              // size="large" // Default size might be better for drawer
              items={tabItems}
              destroyInactiveTabPane // Good for performance if tabs have heavy content
            />
          )
        )}
      </Spin>
    </Drawer>
  );
};

export default ApplicantReviewDrawer;